import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-hot-toast';
import { 
  Image, 
  Video, 
  Loader2, 
  RefreshCw, 
  Search, 
  Download, 
  Trash2, 
  Copy, 
  Eye,
  Calendar,
  HardDrive,
  Grid3X3,
  List,
  Filter,
  X,
  ChevronLeft,
  ChevronRight,
  Upload,
  FolderOpen,
  Plus,
  CheckCircle2,
  AlertCircle,
  FileImage,
  FileVideo,
  Sparkles,
  Wand2,
  Send,
  RotateCcw,
  ArrowRight,
  CheckSquare,
  Square
} from 'lucide-react';

// Enhancement presets
const ENHANCEMENT_PRESETS = [
  { id: 'enhance', name: 'Auto Enhance', prompt: 'Enhance this product image with better lighting, sharper details, and professional color grading. Keep the product exactly the same but make it look more premium and appealing.' },
  { id: 'brighten', name: 'Brighten', prompt: 'Make this image brighter and more vibrant while maintaining natural colors. Improve exposure and reduce any dark shadows.' },
  { id: 'color', name: 'Color Pop', prompt: 'Make the colors in this image more vivid and eye-catching. Enhance saturation tastefully, add slight warmth, and make it stand out for social media ads.' },
  { id: 'background', name: 'White BG', prompt: 'Replace the background with a clean, pure white studio background while keeping the main product/subject perfectly intact. Professional e-commerce style.' },
  { id: 'gradient', name: 'Gradient BG', prompt: 'Replace the background with a beautiful modern gradient (soft pink to purple or blue to teal). Keep the product intact, professional look.' },
  { id: 'nature', name: 'Nature BG', prompt: 'Place this product in a beautiful natural outdoor setting - garden, forest, or beach. Make it look naturally placed and professionally shot.' },
  { id: 'urban', name: 'Urban BG', prompt: 'Place this product in a modern urban setting - trendy cafe, city street, or contemporary interior. Lifestyle marketing style.' },
  { id: 'minimal', name: 'Minimal BG', prompt: 'Create an ultra-minimalist background - single solid color (soft gray, beige, or pastel), very clean and modern. Focus entirely on the product.' },
  { id: 'lifestyle', name: 'Lifestyle', prompt: 'Transform this product photo into a lifestyle scene. Add a beautiful, contextual background that fits the product category. Make it look like a professional marketing photo.' },
  { id: 'premium', name: 'Premium', prompt: 'Transform this into a luxury, high-end product shot. Add subtle shadows, premium lighting effects, and a sophisticated feel. Magazine-quality result.' },
  { id: 'magazine', name: 'Magazine', prompt: 'Style this like a high-end magazine cover or editorial shot. Dramatic lighting, perfect composition, fashion/lifestyle magazine quality.' },
  { id: '3dpop', name: '3D Pop', prompt: 'Create a 3D pop-out effect where the product appears to jump out of the frame. Add depth, shadows, and a dynamic feel.' },
  { id: 'retro', name: 'Retro', prompt: 'Apply a vintage retro filter - 70s/80s nostalgic look with warm tones, film grain, and faded colors. Hipster aesthetic.' },
  { id: 'neon', name: 'Neon Glow', prompt: 'Add neon glow effects and cyberpunk lighting. Vibrant neon colors (pink, blue, purple) creating a futuristic, nightclub atmosphere.' },
  { id: 'pastel', name: 'Pastel Soft', prompt: 'Transform with soft pastel color palette. Dreamy, gentle tones - soft pink, baby blue, mint green. Instagram aesthetic, very gentle and appealing.' },
  { id: 'dark', name: 'Dark Moody', prompt: 'Create a dark, moody, dramatic atmosphere. Deep shadows, rich blacks, selective lighting highlighting the product. Luxury and mystery.' },
  { id: 'summer', name: 'Summer', prompt: 'Add bright summer vibes - sunny lighting, beach/pool atmosphere, warm golden tones, vacation feeling. Perfect for summer campaigns.' },
  { id: 'autumn', name: 'Autumn', prompt: 'Apply warm autumn colors - orange, brown, golden tones. Cozy fall atmosphere with warm lighting. Perfect for fall campaigns.' },
  { id: 'winter', name: 'Winter', prompt: 'Create cozy winter atmosphere - soft snow, warm indoor lighting, holiday feeling. Blue-white tones with warm accent lighting.' },
  { id: 'holiday', name: 'Holiday', prompt: 'Add festive holiday atmosphere - subtle sparkles, warm lights, celebration feeling. Works for Christmas, New Year, or any festive occasion.' },
  { id: 'shadow', name: 'Add Shadow', prompt: 'Add professional product shadow - soft natural shadow beneath the product for depth. Clean e-commerce style with proper grounding.' },
];

export default function MetaMediaLibraryLab() {
  const { selectedBrand } = useAuth();
  const fileInputRef = useRef(null);
  
  const brandId = useMemo(() => {
    return selectedBrand?.id || localStorage.getItem('currentBrandId') || sessionStorage.getItem('currentBrandId') || '';
  }, [selectedBrand?.id]);
  
  // Connection state
  const [adAccountId, setAdAccountId] = useState(null);
  const [adAccountName, setAdAccountName] = useState('');
  const [connectionLoading, setConnectionLoading] = useState(true);
  
  // Media state
  const [images, setImages] = useState([]);
  const [videos, setVideos] = useState([]);
  const [loadingImages, setLoadingImages] = useState(false);
  const [loadingVideos, setLoadingVideos] = useState(false);
  
  // UI state
  const [activeTab, setActiveTab] = useState('images'); // 'images' | 'videos'
  const [viewMode, setViewMode] = useState('grid'); // 'grid' | 'list'
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [showPreview, setShowPreview] = useState(false);

  // Bulk selection / download state
  const [selectMode, setSelectMode] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState(() => new Set());
  const [isBulkDownloading, setIsBulkDownloading] = useState(false);

  // Upload state
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadFiles, setUploadFiles] = useState([]); // {file, status: 'pending'|'uploading'|'success'|'error', progress, error}
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  
  // AI Generator state
  const [showAIModal, setShowAIModal] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedImage, setGeneratedImage] = useState(null); // { mimeType, data }
  const [aiError, setAiError] = useState('');
  const [isUploadingAI, setIsUploadingAI] = useState(false);
  
  // Enhance state
  const [showEnhanceModal, setShowEnhanceModal] = useState(false);
  const [enhanceImage, setEnhanceImage] = useState(null);
  const [selectedPreset, setSelectedPreset] = useState(null);
  const [customEnhancePrompt, setCustomEnhancePrompt] = useState('');
  const [isEnhancing, setIsEnhancing] = useState(false);
  const [enhancedImage, setEnhancedImage] = useState(null);
  const [enhanceError, setEnhanceError] = useState('');
  const [isUploadingEnhanced, setIsUploadingEnhanced] = useState(false);
  
  // Pagination
  const [imagesCursor, setImagesCursor] = useState(null);
  const [videosCursor, setVideosCursor] = useState(null);
  const [hasMoreImages, setHasMoreImages] = useState(false);
  const [hasMoreVideos, setHasMoreVideos] = useState(false);
  
  // Merchant Center state (for product images)
  const [merchantId, setMerchantId] = useState(null);
  const [merchantProducts, setMerchantProducts] = useState([]);

  // Resolve Meta Ads connection on mount
  useEffect(() => {
    if (selectedBrand?.id) {
      resolveConnection();
    }
  }, [selectedBrand?.id]);

  // Fetch media when ad account is resolved
  useEffect(() => {
    if (adAccountId) {
      fetchImages();
      fetchVideos();
    }
  }, [adAccountId]);

  const resolveConnection = async () => {
    setConnectionLoading(true);
    try {
      const resp = await fetch('https://api.glowytics.com/api/auth/connections', {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-Brand-ID': selectedBrand.id
        }
      });
      const data = await resp.json();
      const connections = Array.isArray(data) ? data : (Array.isArray(data?.connections) ? data.connections : []);
      
      const metaAdsConn = connections.find((c) => c?.platform === 'META_ADS' && c?.status === 'ACTIVE');
      
      if (metaAdsConn) {
        // customerId is the ad account ID (without act_ prefix usually)
        const accountId = metaAdsConn.customerId;
        setAdAccountId(accountId);
        setAdAccountName(metaAdsConn.accountName || `Ad Account ${accountId}`);
      } else {
        toast.error('Meta Ads connection not found. Please connect from Settings.');
      }
      
      // Also check for Merchant Center connection
      const mcConn = connections.find((c) => c?.platform === 'MERCHANT_CENTER' && c?.status === 'ACTIVE');
      if (mcConn?.customerId) {
        setMerchantId(mcConn.customerId);
        // Fetch merchant products in background
        fetchMerchantProducts(mcConn.customerId);
      }
    } catch (err) {
      console.error('Connection resolve error:', err);
      toast.error('Failed to get connection info');
    } finally {
      setConnectionLoading(false);
    }
  };

  // Fetch Merchant Center products for AI analysis
  const fetchMerchantProducts = async (mcId) => {
    try {
      // Try live API first (fetches directly from Google)
      const resp = await fetch(
        `https://api.glowytics.com/api/auth/merchant-center/products/preview?merchantId=${encodeURIComponent(mcId)}&maxResults=50`,
        {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'X-Brand-ID': brandId
          }
        }
      );
      const data = await resp.json();
      
      if (data.products && Array.isArray(data.products)) {
        // Map to standardized format with image URLs
        const products = data.products
          .filter(p => p.imageLink)
          .map(p => ({
            id: p.offerId || p.id,
            title: p.title,
            imageUrl: p.imageLink,
            additionalImages: p.additionalImageLinks || [],
            category: p.googleProductCategory,
            brand: p.brand
          }));
        setMerchantProducts(products);
        console.log(`Loaded ${products.length} merchant products with images from live API`);
      }
    } catch (err) {
      console.error('Fetch merchant products error:', err);
      // Fallback: try DB endpoint
      try {
        const dbResp = await fetch(
          `https://api.glowytics.com/api/google-ads/merchant-center/products/preview/db?merchantId=${encodeURIComponent(mcId)}&limit=50`,
          {
            method: 'GET',
            credentials: 'include',
            headers: {
              'Content-Type': 'application/json',
              'X-Brand-ID': brandId
            }
          }
        );
        const dbData = await dbResp.json();
        if (dbData.products && Array.isArray(dbData.products)) {
          const products = dbData.products
            .filter(p => p.image_link)
            .map(p => ({
              id: p.offer_id || p.id,
              title: p.title,
              imageUrl: p.image_link,
              category: p.google_product_category || p.product_type,
              brand: p.brand
            }));
          setMerchantProducts(products);
          console.log(`Loaded ${products.length} merchant products with images from DB`);
        }
      } catch (dbErr) {
        console.error('Fetch merchant products from DB also failed:', dbErr);
      }
    }
  };

  const fetchImages = async (cursor = null) => {
    if (!adAccountId) return;
    setLoadingImages(true);
    
    try {
      let url = `https://api.glowytics.com/api/meta-ads/media-library/images?adAccountId=${adAccountId}`;
      if (cursor) {
        url += `&after=${cursor}`;
      }
      
      const resp = await fetch(url, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-Brand-ID': selectedBrand.id
        }
      });
      
      const data = await resp.json();
      
      if (data.success) {
        if (cursor) {
          setImages(prev => [...prev, ...data.images]);
        } else {
          setImages(data.images || []);
        }
        setImagesCursor(data.paging?.cursors?.after || null);
        setHasMoreImages(!!data.paging?.next);
      } else {
        toast.error(data.error || 'Failed to load images');
      }
    } catch (err) {
      console.error('Fetch images error:', err);
      toast.error('Error loading images');
    } finally {
      setLoadingImages(false);
    }
  };

  const fetchVideos = async (cursor = null) => {
    if (!adAccountId) return;
    setLoadingVideos(true);
    
    try {
      let url = `https://api.glowytics.com/api/meta-ads/media-library/videos?adAccountId=${adAccountId}`;
      if (cursor) {
        url += `&after=${cursor}`;
      }
      
      const resp = await fetch(url, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-Brand-ID': selectedBrand.id
        }
      });
      
      const data = await resp.json();
      
      if (data.success) {
        if (cursor) {
          setVideos(prev => [...prev, ...data.videos]);
        } else {
          setVideos(data.videos || []);
        }
        setVideosCursor(data.paging?.cursors?.after || null);
        setHasMoreVideos(!!data.paging?.next);
      } else {
        toast.error(data.error || 'Failed to load videos');
      }
    } catch (err) {
      console.error('Fetch videos error:', err);
      toast.error('Error loading videos');
    } finally {
      setLoadingVideos(false);
    }
  };

  const handleRefresh = () => {
    setImages([]);
    setVideos([]);
    setImagesCursor(null);
    setVideosCursor(null);
    fetchImages();
    fetchVideos();
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied!`);
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '-';
    const kb = bytes / 1024;
    if (kb < 1024) return `${kb.toFixed(1)} KB`;
    return `${(kb / 1024).toFixed(2)} MB`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('tr-TR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Filter media by search term
  const filteredImages = images.filter(img => 
    (img.name || img.hash || '').toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  const filteredVideos = videos.filter(vid => 
    (vid.title || vid.id || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const currentMedia = activeTab === 'images' ? filteredImages : filteredVideos;
  const isLoading = activeTab === 'images' ? loadingImages : loadingVideos;
  const hasMore = activeTab === 'images' ? hasMoreImages : hasMoreVideos;

  // ── Bulk selection helpers ─────────────────────────────────────────────
  const getKey = (item) => String(item?.id || item?.hash || item?.url || '');

  const toggleSelect = (item) => {
    const k = getKey(item);
    if (!k) return;
    setSelectedKeys(prev => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k); else next.add(k);
      return next;
    });
  };

  const allShownSelected = currentMedia.length > 0 && currentMedia.every(it => selectedKeys.has(getKey(it)));

  const toggleSelectAllShown = () => {
    setSelectedKeys(prev => {
      const next = new Set(prev);
      if (allShownSelected) {
        currentMedia.forEach(it => next.delete(getKey(it)));
      } else {
        currentMedia.forEach(it => next.add(getKey(it)));
      }
      return next;
    });
  };

  const clearSelection = () => setSelectedKeys(new Set());

  const exitSelectMode = () => { setSelectMode(false); clearSelection(); };

  // Download the selected media as a single ZIP. Uses the backend image-proxy
  // (POST /api/ai/image-proxy, max 10 urls/call) to bypass Meta CDN CORS.
  const downloadSelected = async () => {
    const selectedItems = currentMedia.filter(it => selectedKeys.has(getKey(it)));
    if (selectedItems.length === 0) {
      toast.error('Önce en az bir görsel seçin');
      return;
    }
    setIsBulkDownloading(true);
    toast.loading(`${selectedItems.length} öğe hazırlanıyor...`, { id: 'bulk-dl' });
    try {
      const JSZip = (await import('jszip')).default;
      const zip = new JSZip();

      const urlItems = selectedItems.map((it, i) => {
        const u = activeTab === 'images'
          ? (it.url || it.url_128)
          : (it.source || it.url || it.picture);
        return { url: u, name: (it.name || it.hash || it.id || `media-${i + 1}`) };
      }).filter(x => x.url);

      let added = 0, failed = 0;
      const usedNames = new Set();

      for (let i = 0; i < urlItems.length; i += 10) {
        const batch = urlItems.slice(i, i + 10);
        const resp = await fetch('https://api.glowytics.com/api/ai/image-proxy', {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'X-Brand-ID': selectedBrand?.id || ''
          },
          body: JSON.stringify({ urls: batch.map(b => b.url) })
        });
        const data = await resp.json();
        const results = Array.isArray(data?.results) ? data.results : [];
        results.forEach((r, idx) => {
          const meta = batch[idx] || {};
          if (r?.success && r?.base64) {
            const ct = String(r.contentType || '');
            const ext = ct.includes('png') ? 'png'
              : ct.includes('webp') ? 'webp'
              : ct.includes('gif') ? 'gif'
              : ct.includes('mp4') || activeTab === 'videos' ? 'mp4'
              : 'jpg';
            const base = String(meta.name).replace(/[^\w.-]+/g, '_').slice(0, 60) || 'media';
            let fname = `${base}.${ext}`;
            let n = 1;
            while (usedNames.has(fname)) { fname = `${base}_${n++}.${ext}`; }
            usedNames.add(fname);
            zip.file(fname, r.base64, { base64: true });
            added++;
          } else {
            failed++;
          }
        });
        toast.loading(`${Math.min(i + 10, urlItems.length)}/${urlItems.length} işlendi...`, { id: 'bulk-dl' });
      }

      if (added === 0) {
        toast.error('Hiçbir öğe indirilemedi', { id: 'bulk-dl' });
        return;
      }

      const blob = await zip.generateAsync({ type: 'blob' });
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `meta-media-${(adAccountName || adAccountId || 'export')}-${Date.now()}.zip`;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      setTimeout(() => { document.body.removeChild(link); URL.revokeObjectURL(blobUrl); }, 200);

      toast.success(`${added} öğe ZIP olarak indirildi${failed ? ` (${failed} başarısız)` : ''}`, { id: 'bulk-dl' });
    } catch (e) {
      console.error('[MetaMediaLibrary] bulk download error', e);
      toast.error('İndirme hatası: ' + (e?.message || e), { id: 'bulk-dl' });
    } finally {
      setIsBulkDownloading(false);
    }
  };

  const loadMore = () => {
    if (activeTab === 'images' && imagesCursor) {
      fetchImages(imagesCursor);
    } else if (activeTab === 'videos' && videosCursor) {
      fetchVideos(videosCursor);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  // UPLOAD HANDLERS
  // ─────────────────────────────────────────────────────────────────────────
  
  const isImageFile = (file) => file.type.startsWith('image/');
  const isVideoFile = (file) => file.type.startsWith('video/');
  
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };
  
  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };
  
  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files).filter(f => isImageFile(f) || isVideoFile(f));
    if (files.length > 0) {
      addFilesToUpload(files);
    }
  };
  
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files).filter(f => isImageFile(f) || isVideoFile(f));
    if (files.length > 0) {
      addFilesToUpload(files);
    }
    // Reset input
    e.target.value = '';
  };
  
  const addFilesToUpload = (files) => {
    const newFiles = files.map(file => ({
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      file,
      name: file.name,
      size: file.size,
      type: isImageFile(file) ? 'image' : 'video',
      status: 'pending',
      progress: 0,
      error: null,
      preview: isImageFile(file) ? URL.createObjectURL(file) : null
    }));
    setUploadFiles(prev => [...prev, ...newFiles]);
    setShowUploadModal(true);
  };
  
  const removeFileFromUpload = (id) => {
    setUploadFiles(prev => {
      const file = prev.find(f => f.id === id);
      if (file?.preview) {
        URL.revokeObjectURL(file.preview);
      }
      return prev.filter(f => f.id !== id);
    });
  };
  
  const uploadSingleFile = async (uploadFile, retryCount = 0) => {
    const MAX_RETRIES = 2;
    const formData = new FormData();
    formData.append('file', uploadFile.file);
    formData.append('adAccountId', adAccountId);
    formData.append('type', uploadFile.type);
    
    try {
      // Update status to uploading
      setUploadFiles(prev => prev.map(f => 
        f.id === uploadFile.id ? { ...f, status: 'uploading', progress: retryCount > 0 ? 5 : 10 } : f
      ));
      
      const endpoint = uploadFile.type === 'image' 
        ? 'https://api.glowytics.com/api/meta-ads/media-library/upload-image'
        : 'https://api.glowytics.com/api/meta-ads/media-library/upload-video';
      
      // Create AbortController for timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 120000); // 2 minute timeout
      
      const resp = await fetch(endpoint, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'X-Brand-ID': selectedBrand.id
        },
        body: formData,
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      const data = await resp.json();
      
      if (data.success) {
        setUploadFiles(prev => prev.map(f => 
          f.id === uploadFile.id ? { ...f, status: 'success', progress: 100 } : f
        ));
        return true;
      } else {
        setUploadFiles(prev => prev.map(f => 
          f.id === uploadFile.id ? { ...f, status: 'error', error: data.error || 'Upload failed' } : f
        ));
        return false;
      }
    } catch (err) {
      // Retry on network errors
      if (retryCount < MAX_RETRIES && (err.name === 'AbortError' || err.message === 'Failed to fetch')) {
        console.log(`Retrying upload for ${uploadFile.name} (attempt ${retryCount + 2})`);
        await new Promise(r => setTimeout(r, 1000)); // Wait 1 second before retry
        return uploadSingleFile(uploadFile, retryCount + 1);
      }
      
      const errorMsg = err.name === 'AbortError' 
        ? 'Upload timeout - file too large or slow connection'
        : err.message === 'Failed to fetch'
          ? 'Network error - check connection or try smaller file'
          : err.message;
      
      setUploadFiles(prev => prev.map(f => 
        f.id === uploadFile.id ? { ...f, status: 'error', error: errorMsg } : f
      ));
      return false;
    }
  };
  
  const startUpload = async () => {
    const pendingFiles = uploadFiles.filter(f => f.status === 'pending');
    if (pendingFiles.length === 0) return;
    
    setIsUploading(true);
    
    let successCount = 0;
    let errorCount = 0;
    
    // Upload files sequentially to avoid overwhelming the server
    for (const file of pendingFiles) {
      const success = await uploadSingleFile(file);
      if (success) successCount++;
      else errorCount++;
    }
    
    setIsUploading(false);
    
    if (successCount > 0) {
      toast.success(`${successCount} file${successCount > 1 ? 's' : ''} uploaded successfully!`);
      // Refresh media list
      handleRefresh();
    }
    if (errorCount > 0) {
      toast.error(`${errorCount} file${errorCount > 1 ? 's' : ''} failed to upload`);
    }
  };
  
  const clearCompletedUploads = () => {
    setUploadFiles(prev => {
      prev.filter(f => f.status === 'success' || f.status === 'error').forEach(f => {
        if (f.preview) URL.revokeObjectURL(f.preview);
      });
      return prev.filter(f => f.status === 'pending' || f.status === 'uploading');
    });
  };
  
  const closeUploadModal = () => {
    if (isUploading) return;
    // Clean up previews
    uploadFiles.forEach(f => {
      if (f.preview) URL.revokeObjectURL(f.preview);
    });
    setUploadFiles([]);
    setShowUploadModal(false);
  };

  // Upload Modal
  const UploadModal = () => {
    if (!showUploadModal) return null;
    
    const pendingCount = uploadFiles.filter(f => f.status === 'pending').length;
    const completedCount = uploadFiles.filter(f => f.status === 'success' || f.status === 'error').length;
    
    return (
      <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={closeUploadModal}>
        <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[85vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b">
            <h3 className="font-semibold text-gray-900 flex items-center gap-2">
              <Upload className="w-5 h-5" />
              Upload Media
            </h3>
            <button 
              onClick={closeUploadModal} 
              disabled={isUploading}
              className="p-2 hover:bg-gray-100 rounded-lg disabled:opacity-50"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          
          {/* Drop Zone */}
          <div 
            className={`m-4 border-2 border-dashed rounded-xl p-8 text-center transition-colors ${
              isDragging 
                ? 'border-blue-500 bg-blue-50' 
                : 'border-gray-300 hover:border-gray-400'
            }`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <div className="flex flex-col items-center gap-3">
              <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center">
                <Upload className="w-7 h-7 text-gray-500" />
              </div>
              <div>
                <p className="font-medium text-gray-900">Drag & drop files here</p>
                <p className="text-sm text-gray-500 mt-1">or select from your computer</p>
              </div>
              <button
                onClick={() => fileInputRef.current?.click()}
                className="px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors text-sm font-medium"
              >
                Select Files
              </button>
              <p className="text-xs text-gray-400">PNG, JPG, GIF, MP4, MOV supported</p>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*,video/*"
              onChange={handleFileSelect}
              className="hidden"
            />
          </div>
          
          {/* File List */}
          {uploadFiles.length > 0 && (
            <div className="flex-1 overflow-auto px-4 pb-4">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium text-gray-700">
                  {uploadFiles.length} file{uploadFiles.length > 1 ? 's' : ''} selected
                </span>
                {completedCount > 0 && (
                  <button
                    onClick={clearCompletedUploads}
                    className="text-xs text-gray-500 hover:text-gray-700"
                  >
                    Clear completed
                  </button>
                )}
              </div>
              
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {uploadFiles.map((f) => (
                  <div 
                    key={f.id}
                    className={`flex items-center gap-3 p-3 rounded-lg border ${
                      f.status === 'success' ? 'bg-green-50 border-green-200' :
                      f.status === 'error' ? 'bg-red-50 border-red-200' :
                      f.status === 'uploading' ? 'bg-blue-50 border-blue-200' :
                      'bg-gray-50 border-gray-200'
                    }`}
                  >
                    {/* Preview/Icon */}
                    <div className="w-12 h-12 rounded-lg overflow-hidden bg-gray-200 flex-shrink-0 flex items-center justify-center">
                      {f.preview ? (
                        <img src={f.preview} alt="" className="w-full h-full object-cover" />
                      ) : f.type === 'video' ? (
                        <FileVideo className="w-6 h-6 text-gray-500" />
                      ) : (
                        <FileImage className="w-6 h-6 text-gray-500" />
                      )}
                    </div>
                    
                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{f.name}</p>
                      <p className="text-xs text-gray-500">
                        {formatFileSize(f.size)} • {f.type === 'image' ? 'Image' : 'Video'}
                      </p>
                      {f.error && (
                        <p className="text-xs text-red-600 mt-1">{f.error}</p>
                      )}
                    </div>
                    
                    {/* Status */}
                    <div className="flex-shrink-0">
                      {f.status === 'pending' && (
                        <button
                          onClick={() => removeFileFromUpload(f.id)}
                          className="p-1.5 hover:bg-gray-200 rounded-lg"
                        >
                          <X className="w-4 h-4 text-gray-500" />
                        </button>
                      )}
                      {f.status === 'uploading' && (
                        <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />
                      )}
                      {f.status === 'success' && (
                        <CheckCircle2 className="w-5 h-5 text-green-600" />
                      )}
                      {f.status === 'error' && (
                        <AlertCircle className="w-5 h-5 text-red-600" />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Footer */}
          <div className="p-4 border-t bg-gray-50 flex items-center justify-between">
            <button
              onClick={closeUploadModal}
              disabled={isUploading}
              className="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={startUpload}
              disabled={pendingCount === 0 || isUploading}
              className="px-6 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isUploading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4" />
                  Upload {pendingCount} File{pendingCount > 1 ? 's' : ''}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // ─────────────────────────────────────────────────────────────────────────
  // AI IMAGE GENERATOR
  // ─────────────────────────────────────────────────────────────────────────
  
  const generateAIImage = async () => {
    if (!aiPrompt.trim() || isGenerating) return;
    
    setIsGenerating(true);
    setAiError('');
    setGeneratedImage(null);
    
    try {
      // Ensure square format is in the prompt
      let finalPrompt = aiPrompt.trim();
      if (!finalPrompt.toLowerCase().includes('square') && !finalPrompt.toLowerCase().includes('1:1')) {
        finalPrompt = `IMPORTANT: Generate a SQUARE image (1:1 aspect ratio). ${finalPrompt}`;
      }
      
      const resp = await fetch('https://api.glowytics.com/api/ai/gemini/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Brand-ID': brandId
        },
        credentials: 'include',
        body: JSON.stringify({
          model: 'gemini-2.5-flash-image',
          temperature: 0.8,
          messages: [
            {
              role: 'user',
              content: finalPrompt,
              images: []
            }
          ]
        })
      });

      const txt = await resp.text();
      let json;
      try { json = JSON.parse(txt); } catch { json = { message: txt }; }
      
      if (!resp.ok) {
        throw new Error(json?.error || json?.message || resp.statusText);
      }

      if (json?.images && json.images.length > 0) {
        setGeneratedImage(json.images[0]);
        toast.success('Image generated!');
      } else {
        setAiError(json?.message || 'No image was generated. Try a different prompt.');
      }
    } catch (err) {
      console.error('AI generation error:', err);
      setAiError(err.message || 'Failed to generate image');
      toast.error('Generation failed');
    } finally {
      setIsGenerating(false);
    }
  };

  // Multi-size state
  const [allSizeImages, setAllSizeImages] = useState(null); // { square, story, landscape }
  const [isGeneratingAllSizes, setIsGeneratingAllSizes] = useState(false);
  const [previewSize, setPreviewSize] = useState('square'); // which size to preview

  // Generate square image first
  // Generate image in specific aspect ratio using Gemini's aspectRatio parameter
  const generateForAspectRatio = async (basePrompt, aspectRatioKey) => {
    // Map our keys to Gemini's aspectRatio format
    const aspectRatioMap = {
      square: '1:1',
      story: '9:16',
      landscape: '16:9'
    };
    
    const resp = await fetch('https://api.glowytics.com/api/ai/gemini/chat', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId },
      credentials: 'include',
      body: JSON.stringify({
        model: 'gemini-2.5-flash-image',
        temperature: 0.7,
        aspectRatio: aspectRatioMap[aspectRatioKey], // Pass aspect ratio to API
        messages: [{ 
          role: 'user', 
          content: `Create a professional promotional image for advertising. ${basePrompt}. High quality, suitable for social media ads.`
        }]
      })
    });
    const txt = await resp.text();
    let json; try { json = JSON.parse(txt); } catch { json = { message: txt }; }
    if (!resp.ok) throw new Error(json?.error || 'Generation failed');
    return json?.images?.[0] || null;
  };

  // Generate all 3 sizes - each aspect ratio generated separately with same concept
  const generateAllSizes = async () => {
    if (!aiPrompt.trim() || isGeneratingAllSizes) return;
    
    setIsGeneratingAllSizes(true);
    setAllSizeImages(null);
    setGeneratedImage(null);
    setPreviewSize('square');
    
    const basePrompt = aiPrompt.trim();
    const results = { square: null, story: null, landscape: null };
    
    try {
      // Generate all 3 aspect ratios with the same concept
      toast.loading('Creating square (1:1)...', { id: 'gen-progress' });
      results.square = await generateForAspectRatio(basePrompt, 'square');
      setAllSizeImages({ ...results });
      setGeneratedImage(results.square);
      
      toast.loading('Creating story (9:16)...', { id: 'gen-progress' });
      results.story = await generateForAspectRatio(basePrompt, 'story');
      setAllSizeImages({ ...results });
      
      toast.loading('Creating landscape (16:9)...', { id: 'gen-progress' });
      results.landscape = await generateForAspectRatio(basePrompt, 'landscape');
      setAllSizeImages({ ...results });
      
      toast.dismiss('gen-progress');
      
      const successCount = [results.square, results.story, results.landscape].filter(Boolean).length;
      toast.success(`${successCount}/3 sizes ready!`);
      
    } catch (err) {
      toast.dismiss('gen-progress');
      console.error('Generate error:', err);
      toast.error(err.message || 'Generation failed');
      // Still show what we have
      if (results.square) {
        setAllSizeImages(results);
        setGeneratedImage(results.square);
      }
    } finally {
      setIsGeneratingAllSizes(false);
    }
  };

  // Download all available sizes
  const downloadAllSizes = async () => {
    if (!allSizeImages) return;
    
    const timestamp = Date.now();
    const sizes = [
      { key: 'square', name: '1x1' },
      { key: 'story', name: '9x16' },
      { key: 'landscape', name: '16x9' }
    ];
    
    // Collect all available images
    const availableImages = sizes.filter(s => allSizeImages[s.key]?.data);
    
    if (availableImages.length === 0) {
      toast.error('No images to download');
      return;
    }
    
    toast.loading(`Downloading ${availableImages.length} images...`, { id: 'download-progress' });
    
    // Download each with longer delay to prevent browser blocking
    for (let i = 0; i < availableImages.length; i++) {
      const size = availableImages[i];
      const img = allSizeImages[size.key];
      
      // Convert base64 to blob for more reliable downloads
      const byteCharacters = atob(img.data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let j = 0; j < byteCharacters.length; j++) {
        byteNumbers[j] = byteCharacters.charCodeAt(j);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: img.mimeType || 'image/png' });
      const blobUrl = URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `ai-${size.name}-${timestamp}.png`;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      setTimeout(() => {
        document.body.removeChild(link);
        URL.revokeObjectURL(blobUrl);
      }, 100);
      
      // Wait longer between downloads to avoid browser blocking
      if (i < availableImages.length - 1) {
        await new Promise(r => setTimeout(r, 800));
      }
    }
    
    toast.dismiss('download-progress');
    toast.success(`Downloaded ${availableImages.length} images!`);
  };

  // Upload all sizes to Meta
  const uploadAllSizesToMeta = async () => {
    if (!allSizeImages || !adAccountId || isUploadingAI) return;
    
    setIsUploadingAI(true);
    const timestamp = Date.now();
    let uploadCount = 0;
    
    try {
      const sizes = [
        { key: 'square', name: 'square-1x1' },
        { key: 'story', name: 'story-9x16' },
        { key: 'landscape', name: 'landscape-16x9' }
      ];
      
      for (const size of sizes) {
        const img = allSizeImages[size.key];
        if (!img?.data) continue;
        
        toast.loading(`Uploading ${size.name}...`, { id: 'upload-progress' });
        
        const byteCharacters = atob(img.data);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: img.mimeType || 'image/png' });
        
        const fileName = `ai-${size.name}-${timestamp}.png`;
        const file = new File([blob], fileName, { type: 'image/png' });
        
        const formData = new FormData();
        formData.append('file', file);
        formData.append('adAccountId', adAccountId);
        formData.append('type', 'image');
        
        const resp = await fetch('https://api.glowytics.com/api/meta-ads/media-library/upload-image', {
          method: 'POST',
          credentials: 'include',
          headers: { 'X-Brand-ID': brandId },
          body: formData
        });
        
        const data = await resp.json();
        if (data.success) uploadCount++;
      }
      
      toast.dismiss('upload-progress');
      toast.success(`${uploadCount}/3 sizes uploaded to Meta!`);
      fetchImages();
    } catch (err) {
      toast.dismiss('upload-progress');
      console.error('Upload error:', err);
      toast.error('Upload failed');
    } finally {
      setIsUploadingAI(false);
    }
  };

  const downloadAIImage = () => {
    if (!generatedImage?.data) return;
    
    const link = document.createElement('a');
    link.href = `data:${generatedImage.mimeType || 'image/png'};base64,${generatedImage.data}`;
    link.download = `ai-generated-${Date.now()}.${generatedImage.mimeType?.includes('jpeg') ? 'jpg' : 'png'}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Image downloaded!');
  };

  const uploadAIToMeta = async () => {
    if (!generatedImage?.data || !adAccountId || isUploadingAI) return;
    
    setIsUploadingAI(true);
    try {
      const byteCharacters = atob(generatedImage.data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: generatedImage.mimeType || 'image/png' });
      
      const fileName = `ai-generated-${Date.now()}.${generatedImage.mimeType?.includes('jpeg') ? 'jpg' : 'png'}`;
      const file = new File([blob], fileName, { type: generatedImage.mimeType || 'image/png' });
      
      const formData = new FormData();
      formData.append('file', file);
      formData.append('adAccountId', adAccountId);
      formData.append('type', 'image');
      
      const resp = await fetch('https://api.glowytics.com/api/meta-ads/media-library/upload-image', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'X-Brand-ID': brandId
        },
        body: formData
      });
      
      const data = await resp.json();
      
      if (data.success) {
        toast.success('Image uploaded to Meta!');
        fetchImages();
        closeAIModal();
      } else {
        throw new Error(data.error || 'Upload failed');
      }
    } catch (err) {
      console.error('Upload to Meta error:', err);
      toast.error(err.message || 'Failed to upload');
    } finally {
      setIsUploadingAI(false);
    }
  };

  const closeAIModal = () => {
    if (isGenerating || isUploadingAI || isGeneratingAllSizes) return;
    setShowAIModal(false);
    setAiPrompt('');
    setGeneratedImage(null);
    setAllSizeImages(null);
    setAiError('');
  };

  const resetAIGeneration = () => {
    setGeneratedImage(null);
    setAllSizeImages(null);
    setAiError('');
    setPreviewSize('square');
  };

  // Dynamic AI-generated prompts
  const [quickIdeas, setQuickIdeas] = useState([]);
  const [isGeneratingIdeas, setIsGeneratingIdeas] = useState(false);
  const [ideasGenerated, setIdeasGenerated] = useState(false);

  // Convert image URL to base64 for Gemini
  const imageUrlToBase64 = async (url) => {
    try {
      const resp = await fetch(url);
      const blob = await resp.blob();
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const dataUrl = reader.result;
          const [prefix, base64] = dataUrl.split(',');
          const mimeType = (prefix.match(/data:([^;]+);base64/i)?.[1]) || 'image/jpeg';
          resolve({ mimeType, data: base64 });
        };
        reader.onerror = () => reject(new Error('Failed'));
        reader.readAsDataURL(blob);
      });
    } catch {
      return null;
    }
  };

  // Store brand analysis for reuse
  const [brandAnalysis, setBrandAnalysis] = useState(null);
  
  // Helper function to get random items from array
  const getRandomItems = (arr, count) => {
    if (!arr || arr.length === 0) return [];
    const shuffled = [...arr].sort(() => Math.random() - 0.5);
    return shuffled.slice(0, Math.min(count, arr.length));
  };

  const generateQuickIdeas = async () => {
    if (isGeneratingIdeas || ideasGenerated) return;
    
    setIsGeneratingIdeas(true);
    try {
      const brandName = selectedBrand?.name || adAccountName || 'brand';
      const websiteUrl = selectedBrand?.websiteUrl || '';
      
      // STEP 1: Get RANDOM images from Meta Media Library (not first 8)
      const sampleImages = [];
      const randomMetaImages = getRandomItems(images, 8);
      
      for (const img of randomMetaImages) {
        const url = img.url_128 || img.url;
        if (url) {
          const base64 = await imageUrlToBase64(url);
          if (base64) sampleImages.push(base64);
        }
      }
      
      // STEP 2: Get RANDOM product images from Merchant Center
      const productImages = [];
      const randomProducts = getRandomItems(merchantProducts, 10);
      
      for (const product of randomProducts) {
        if (product.imageUrl) {
          const base64 = await imageUrlToBase64(product.imageUrl);
          if (base64) productImages.push(base64);
        }
      }
      
      // Combine all images (Meta Library + Merchant Center products)
      const allImages = [...sampleImages, ...productImages];
      const hasExamples = allImages.length > 0;
      const hasMetaImages = sampleImages.length > 0;
      const hasProductImages = productImages.length > 0;
      
      // STEP 3: First, analyze the brand deeply using ALL images
      let analysis = brandAnalysis;
      
      if (!analysis) {
        const analysisResp = await fetch('https://api.glowytics.com/api/ai/gemini/chat', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Brand-ID': brandId
          },
          credentials: 'include',
          body: JSON.stringify({
            model: 'gemini-2.5-flash',
            temperature: 0.3,
            messages: [{
              role: 'user',
              content: `Analyze this brand comprehensively based on the images I'm showing you:

Brand Name: ${brandName}
Website Domain: ${websiteUrl || 'N/A'}

IMAGE SOURCES:
${hasMetaImages ? `- ${sampleImages.length} images from their advertising/marketing media library` : ''}
${hasProductImages ? `- ${productImages.length} actual product images from their e-commerce catalog` : ''}

IMPORTANT: Focus primarily on the IMAGES to understand what this brand sells and their visual style. The website domain is just a hint - derive everything from the actual images.

ANALYZE AND RESPOND IN THIS EXACT FORMAT:
INDUSTRY: [e.g., Fashion, Electronics, Food, Beauty, Home Decor, Toys, etc.]
PRODUCTS: [list 5-8 specific product types you can SEE in the images]
TARGET: [target audience - age, gender, lifestyle based on product types]
STYLE: [brand visual style from images - minimalist, luxury, playful, colorful, etc.]
COLORS: [dominant brand colors you see in the images]
MOOD: [brand mood - professional, fun, elegant, casual, kid-friendly, etc.]
USP: [unique selling point or brand positioning based on product range]

Be VERY specific based on what you actually SEE in the images.`,
              images: allImages
            }]
          })
        });
        
        const analysisTxt = await analysisResp.text();
        let analysisJson;
        try { analysisJson = JSON.parse(analysisTxt); } catch { analysisJson = { message: analysisTxt }; }
        analysis = analysisJson?.message || '';
        setBrandAnalysis(analysis);
        console.log('Brand analysis:', analysis);
      }
      
      // STEP 4: Generate specific prompts based on analysis
      const resp = await fetch('https://api.glowytics.com/api/ai/gemini/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Brand-ID': brandId
        },
        credentials: 'include',
        body: JSON.stringify({
          model: 'gemini-2.5-flash',
          temperature: 0.9,
          messages: [{
            role: 'user',
            content: `Based on this brand analysis:
${analysis}

Brand: ${brandName}
${hasExamples ? `\nI'm showing you ${allImages.length} images (${sampleImages.length} marketing visuals + ${productImages.length} actual product photos).` : ''}

Create 10 UNIQUE, SPECIFIC image prompts for social media ads.

CRITICAL RULES:
1. Each prompt must be for a SPECIFIC product type you SEE in the images
2. Reference ACTUAL visual elements from the images (product shapes, colors, textures, materials)
3. Each prompt MUST be DIFFERENT - vary: product type, setting, mood, composition
4. Include specific details: exact lighting type, background description, mood, styling
5. Make prompts actionable for AI image generation
6. NO generic prompts - be SPECIFIC to THIS brand's actual products
7. Consider the brand's apparent target audience (e.g., if toys, think kid-friendly)

FORMAT (one per line, no numbers, 40-60 words each):
- 2 product hero shots (clean, detailed, studio style for the actual products)
- 2 lifestyle shots (product in real use scenarios)  
- 2 mood/atmosphere shots (brand feeling, aesthetic)
- 2 promotional/campaign style (sale, new arrival, seasonal)
- 2 creative/artistic shots (unique angles, playful compositions)

Write the prompts now:`,
            images: allImages
          }]
        })
      });

      const txt = await resp.text();
      let json;
      try { json = JSON.parse(txt); } catch { json = { message: txt }; }
      
      if (!resp.ok) throw new Error(json?.error || 'Failed to generate');

      const message = json?.message || '';
      console.log('Gemini ideas response:', message);
      
      // Parse line by line
      let prompts = message
        .split('\n')
        .map(line => line.trim())
        .filter(line => line && line.length > 20)
        .map(line => line.replace(/^\d+[\.\)]\s*/, '').replace(/^[-*•]\s*/, '').replace(/^\*\*.*?\*\*:?\s*/, '').trim())
        .filter(line => line.length > 20 && line.length < 300 && !line.includes('FORMAT') && !line.includes('RULES'));
      
      prompts = prompts.slice(0, 12);
      
      if (prompts.length >= 5) {
        setQuickIdeas(prompts);
        setIdeasGenerated(true);
        toast.success(`${prompts.length} unique ideas for ${brandName}!`);
      } else {
        // Generate fallback based on analysis if we have it
        const lines = analysis?.split('\n') || [];
        const products = lines.find(l => l.includes('PRODUCTS'))?.replace('PRODUCTS:', '').trim() || 'products';
        const style = lines.find(l => l.includes('STYLE'))?.replace('STYLE:', '').trim() || 'professional';
        
        const fallback = [
          `${brandName} ${products.split(',')[0]?.trim() || 'product'} hero shot, ${style} aesthetic, soft studio lighting, clean white background, centered composition with subtle shadow`,
          `Lifestyle flat lay featuring ${brandName} collection, morning light, marble surface, coffee cup and greenery accents, Instagram-ready composition`,
          `Close-up detail shot of ${brandName} ${products.split(',')[1]?.trim() || 'item'}, macro photography style, highlighting texture and quality`,
          `${brandName} products in real-life setting, cozy home environment, natural window light, aspirational lifestyle mood`,
          `Bold promotional visual for ${brandName}, geometric shapes, brand colors prominent, space for text overlay, modern design`,
          `Minimalist ${brandName} product arrangement, negative space emphasis, ${style} feel, subtle gradient background`,
        ];
        setQuickIdeas(fallback);
        setIdeasGenerated(true);
        toast.success('Ideas ready!');
      }
    } catch (err) {
      console.error('Generate ideas error:', err);
      const brandName = selectedBrand?.name || adAccountName || 'brand';
      const fallback = [
        `Professional ${brandName} product photography, three-point lighting setup, seamless white backdrop, product centered with reflection`,
        `${brandName} lifestyle composition, product in use scenario, natural lighting, authentic setting, lifestyle appeal`,
        `Detail macro shot of ${brandName} craftsmanship, shallow depth of field, highlighting quality and texture`,
        `${brandName} seasonal campaign visual, trending color palette, modern graphic elements, promotional mood`,
        `Artistic ${brandName} still life, creative composition, dramatic lighting, editorial style photography`,
        `${brandName} collection showcase, multiple products arranged elegantly, cohesive color story, catalog style`,
      ];
      setQuickIdeas(fallback);
      setIdeasGenerated(true);
      toast.success('Ideas ready!');
    } finally {
      setIsGeneratingIdeas(false);
    }
  };

  // Auto-generate ideas when AI modal opens
  useEffect(() => {
    if (showAIModal && !ideasGenerated && !isGeneratingIdeas && brandId) {
      generateQuickIdeas();
    }
  }, [showAIModal, ideasGenerated, isGeneratingIdeas, brandId]);

  // AI Modal
  const AIModal = () => {
    if (!showAIModal) return null;
    
    return (
      <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={closeAIModal}>
        <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b bg-gray-50">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gray-900 rounded-xl flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">AI Image Generator</h3>
                <p className="text-gray-500 text-sm">Create images with AI</p>
              </div>
            </div>
            <button 
              onClick={closeAIModal} 
              disabled={isGenerating || isUploadingAI}
              className="p-2 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50"
            >
              <X className="w-5 h-5 text-gray-600" />
            </button>
          </div>
          
          {/* Content */}
          <div className="flex-1 overflow-auto p-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Left - Prompt */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Describe your image</label>
                  <textarea
                    value={aiPrompt}
                    onChange={(e) => setAiPrompt(e.target.value)}
                    placeholder="E.g., Professional product photo of a white sneaker on a clean background with soft studio lighting..."
                    rows={4}
                    disabled={isGenerating}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none disabled:bg-gray-50"
                  />
                </div>
                
                {/* Quick Ideas - AI Generated */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Quick Ideas {quickIdeas.length > 0 && <span className="text-blue-600">({quickIdeas.length})</span>}
                    </span>
                    {ideasGenerated && (
                      <button
                        onClick={() => { setIdeasGenerated(false); setBrandAnalysis(null); generateQuickIdeas(); }}
                        disabled={isGeneratingIdeas}
                        className="flex items-center gap-1 px-2 py-1 text-gray-500 hover:text-gray-700 text-[10px]"
                      >
                        <RefreshCw className={`w-3 h-3 ${isGeneratingIdeas ? 'animate-spin' : ''}`} />
                        Refresh
                      </button>
                    )}
                  </div>
                  
                  {isGeneratingIdeas ? (
                    <div className="flex flex-col items-center gap-2 py-4">
                      <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
                      <span className="text-xs text-gray-500">
                        {!brandAnalysis ? 'Analyzing brand visuals...' : 'Creating unique ideas...'}
                      </span>
                      <span className="text-[10px] text-gray-400">
                        {!brandAnalysis 
                          ? `📸 ${images.length > 0 ? 'Media library' : ''} ${merchantProducts.length > 0 ? '+ Product catalog' : ''}` 
                          : '✨ Generating specific prompts'}
                      </span>
                    </div>
                  ) : quickIdeas.length > 0 ? (
                    <div className="flex flex-wrap gap-1.5">
                      {quickIdeas.map((prompt, idx) => (
                        <button
                          key={idx}
                          onClick={() => setAiPrompt(prompt)}
                          disabled={isGenerating}
                          className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-[10px] hover:bg-gray-200 transition-colors disabled:opacity-50 text-left"
                        >
                          {prompt.length > 50 ? prompt.slice(0, 50) + '...' : prompt}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-3">
                      <p className="text-xs text-gray-400">Ideas will be generated based on your brand</p>
                    </div>
                  )}
                  
                  {quickIdeas.length > 0 && brandAnalysis && (
                    <div className="mt-3 p-2 bg-gray-50 rounded-lg">
                      <p className="text-[10px] text-gray-500 font-medium mb-1">🎯 Brand Analysis:</p>
                      <p className="text-[9px] text-gray-400 whitespace-pre-line leading-relaxed">
                        {brandAnalysis.split('\n').slice(0, 4).join('\n')}
                      </p>
                      <div className="mt-2 pt-2 border-t border-gray-200 flex items-center gap-3 text-[9px] text-gray-400">
                        <span>📸 {images.length > 0 ? `${Math.min(8, images.length)} media` : '0 media'}</span>
                        <span>🛍️ {merchantProducts.length > 0 ? `${Math.min(10, merchantProducts.length)} products` : 'No products'}</span>
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Generate Button */}
                <button
                  onClick={generateAllSizes}
                  disabled={!aiPrompt.trim() || isGeneratingAllSizes}
                  className="w-full flex items-center justify-center gap-2 py-3 px-6 bg-gray-900 text-white rounded-xl hover:bg-gray-800 transition-all disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                >
                  {isGeneratingAllSizes ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-5 h-5" />
                      Generate
                    </>
                  )}
                </button>
              </div>
              
              {/* Right - Result */}
              <div className="space-y-3">
                <span className="text-sm font-medium text-gray-700">Result</span>
                <div className="relative bg-gray-100 rounded-xl overflow-hidden flex items-center justify-center" style={{ minHeight: '300px' }}>
                  {isGeneratingAllSizes && !allSizeImages?.square ? (
                    <div className="text-center">
                      <Loader2 className="w-12 h-12 text-blue-600 animate-spin mx-auto" />
                      <p className="text-gray-600 mt-4 font-medium">Creating image...</p>
                      <p className="text-gray-400 text-sm mt-1">This takes ~30 seconds</p>
                    </div>
                  ) : allSizeImages?.[previewSize]?.data ? (
                    <img 
                      src={`data:${allSizeImages[previewSize].mimeType || 'image/png'};base64,${allSizeImages[previewSize].data}`}
                      alt={`Generated ${previewSize}`}
                      className="max-w-full max-h-[400px] object-contain"
                    />
                  ) : allSizeImages?.square?.data ? (
                    <img 
                      src={`data:${allSizeImages.square.mimeType || 'image/png'};base64,${allSizeImages.square.data}`}
                      alt="Generated square"
                      className="max-w-full max-h-[400px] object-contain"
                    />
                  ) : aiError ? (
                    <div className="text-center p-4">
                      <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-3">
                        <X className="w-6 h-6 text-red-600" />
                      </div>
                      <p className="text-red-600 text-sm">{aiError}</p>
                    </div>
                  ) : (
                    <div className="text-center">
                      <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-3">
                        <Sparkles className="w-8 h-8 text-gray-400" />
                      </div>
                      <p className="text-gray-500">Your image will appear here</p>
                      <p className="text-gray-400 text-sm mt-1">Enter a prompt and click Generate</p>
                    </div>
                  )}
                </div>
                
                {/* Action Buttons */}
                {allSizeImages?.square && (
                  <div className="space-y-3">
                    {/* Size selector thumbnails - clickable */}
                    <div className="flex gap-2 justify-center items-end">
                      {/* Square */}
                      <button
                        onClick={() => setPreviewSize('square')}
                        className={`text-center transition-all ${previewSize === 'square' ? 'ring-2 ring-blue-500 ring-offset-2' : 'opacity-60 hover:opacity-100'}`}
                      >
                        {allSizeImages.square?.data ? (
                          <img 
                            src={`data:${allSizeImages.square.mimeType || 'image/png'};base64,${allSizeImages.square.data}`} 
                            alt="Square" 
                            className="w-14 h-14 object-cover rounded border border-gray-300"
                          />
                        ) : (
                          <div className="w-14 h-14 bg-gray-200 rounded flex items-center justify-center">
                            <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
                          </div>
                        )}
                        <span className="text-[10px] text-gray-600 font-medium">1:1</span>
                      </button>
                      
                      {/* Story */}
                      <button
                        onClick={() => allSizeImages.story && setPreviewSize('story')}
                        className={`text-center transition-all ${previewSize === 'story' ? 'ring-2 ring-blue-500 ring-offset-2' : 'opacity-60 hover:opacity-100'} ${!allSizeImages.story ? 'cursor-wait' : ''}`}
                      >
                        {allSizeImages.story?.data ? (
                          <img 
                            src={`data:${allSizeImages.story.mimeType || 'image/png'};base64,${allSizeImages.story.data}`} 
                            alt="Story" 
                            className="w-10 h-[70px] object-cover rounded border border-gray-300"
                          />
                        ) : (
                          <div className="w-10 h-[70px] bg-gray-200 rounded flex items-center justify-center">
                            <Loader2 className="w-3 h-3 animate-spin text-gray-400" />
                          </div>
                        )}
                        <span className="text-[10px] text-gray-600 font-medium">9:16</span>
                      </button>
                      
                      {/* Landscape */}
                      <button
                        onClick={() => allSizeImages.landscape && setPreviewSize('landscape')}
                        className={`text-center transition-all ${previewSize === 'landscape' ? 'ring-2 ring-blue-500 ring-offset-2' : 'opacity-60 hover:opacity-100'} ${!allSizeImages.landscape ? 'cursor-wait' : ''}`}
                      >
                        {allSizeImages.landscape?.data ? (
                          <img 
                            src={`data:${allSizeImages.landscape.mimeType || 'image/png'};base64,${allSizeImages.landscape.data}`} 
                            alt="Landscape" 
                            className="w-[70px] h-10 object-cover rounded border border-gray-300"
                          />
                        ) : (
                          <div className="w-[70px] h-10 bg-gray-200 rounded flex items-center justify-center">
                            <Loader2 className="w-3 h-3 animate-spin text-gray-400" />
                          </div>
                        )}
                        <span className="text-[10px] text-gray-600 font-medium">16:9</span>
                      </button>
                    </div>
                    
                    {/* Download & Upload */}
                    <div className="flex gap-2">
                      <button
                        onClick={downloadAllSizes}
                        disabled={!allSizeImages.square}
                        className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors text-sm font-medium disabled:opacity-50"
                      >
                        <Download className="w-4 h-4" />
                        Download
                      </button>
                      <button
                        onClick={uploadAllSizesToMeta}
                        disabled={isUploadingAI || !allSizeImages.square}
                        className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium disabled:opacity-50"
                      >
                        {isUploadingAI ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                        {isUploadingAI ? 'Uploading...' : 'Upload to Meta'}
                      </button>
                      <button
                        onClick={resetAIGeneration}
                        className="flex items-center justify-center py-2.5 px-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ─────────────────────────────────────────────────────────────────────────
  // IMAGE ENHANCER
  // ─────────────────────────────────────────────────────────────────────────
  
  const openEnhanceModal = (image) => {
    setEnhanceImage(image);
    setSelectedPreset(null);
    setCustomEnhancePrompt('');
    setEnhancedImage(null);
    setEnhanceError('');
    setShowEnhanceModal(true);
  };

  const closeEnhanceModal = () => {
    if (isEnhancing || isUploadingEnhanced) return;
    setShowEnhanceModal(false);
    setEnhanceImage(null);
    setSelectedPreset(null);
    setCustomEnhancePrompt('');
    setEnhancedImage(null);
    setEnhanceError('');
  };

  const urlToBase64 = async (url) => {
    try {
      const resp = await fetch(url);
      const blob = await resp.blob();
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const dataUrl = reader.result;
          const [prefix, base64] = dataUrl.split(',');
          const mimeType = (prefix.match(/data:([^;]+);base64/i)?.[1]) || 'image/jpeg';
          resolve({ mimeType, data: base64 });
        };
        reader.onerror = () => reject(new Error('Failed to read image'));
        reader.readAsDataURL(blob);
      });
    } catch (err) {
      throw new Error('Failed to load image: ' + err.message);
    }
  };

  const handleEnhance = async () => {
    if (!enhanceImage || isEnhancing) return;
    
    const prompt = selectedPreset 
      ? ENHANCEMENT_PRESETS.find(p => p.id === selectedPreset)?.prompt 
      : customEnhancePrompt.trim();
    
    if (!prompt) {
      toast.error('Select a preset or enter a prompt');
      return;
    }

    setIsEnhancing(true);
    setEnhanceError('');
    setEnhancedImage(null);

    try {
      const imageUrl = enhanceImage.url || enhanceImage.url_128;
      if (!imageUrl) throw new Error('No image URL available');

      const imageData = await urlToBase64(imageUrl);
      
      const resp = await fetch('https://api.glowytics.com/api/ai/gemini/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Brand-ID': brandId
        },
        credentials: 'include',
        body: JSON.stringify({
          model: 'gemini-2.5-flash-image',
          temperature: 0.7,
          messages: [{ role: 'user', content: prompt, images: [{ mimeType: imageData.mimeType, data: imageData.data }] }]
        })
      });

      const txt = await resp.text();
      let json;
      try { json = JSON.parse(txt); } catch { json = { message: txt }; }
      
      if (!resp.ok) throw new Error(json?.error || json?.message || resp.statusText);

      if (json?.images && json.images.length > 0) {
        setEnhancedImage(json.images[0]);
        toast.success('Image enhanced!');
      } else {
        setEnhanceError(json?.message || 'No image generated. Try different prompt.');
      }
    } catch (err) {
      console.error('Enhance error:', err);
      setEnhanceError(err.message || 'Enhancement failed');
      toast.error('Enhancement failed');
    } finally {
      setIsEnhancing(false);
    }
  };

  const downloadEnhanced = () => {
    if (!enhancedImage?.data) return;
    const link = document.createElement('a');
    link.href = `data:${enhancedImage.mimeType || 'image/png'};base64,${enhancedImage.data}`;
    link.download = `enhanced-${enhanceImage?.hash || 'image'}.${enhancedImage.mimeType?.includes('jpeg') ? 'jpg' : 'png'}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Downloaded!');
  };

  const uploadEnhancedToMeta = async () => {
    if (!enhancedImage?.data || !adAccountId || isUploadingEnhanced) return;
    
    setIsUploadingEnhanced(true);
    try {
      const byteCharacters = atob(enhancedImage.data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: enhancedImage.mimeType || 'image/png' });
      
      const fileName = `enhanced-${Date.now()}.${enhancedImage.mimeType?.includes('jpeg') ? 'jpg' : 'png'}`;
      const file = new File([blob], fileName, { type: enhancedImage.mimeType || 'image/png' });
      
      const formData = new FormData();
      formData.append('file', file);
      formData.append('adAccountId', adAccountId);
      formData.append('type', 'image');
      
      const resp = await fetch('https://api.glowytics.com/api/meta-ads/media-library/upload-image', {
        method: 'POST',
        credentials: 'include',
        headers: { 'X-Brand-ID': brandId },
        body: formData
      });
      
      const data = await resp.json();
      
      if (data.success) {
        toast.success('Uploaded to Meta!');
        fetchImages();
        closeEnhanceModal();
      } else {
        throw new Error(data.error || 'Upload failed');
      }
    } catch (err) {
      console.error('Upload error:', err);
      toast.error(err.message || 'Upload failed');
    } finally {
      setIsUploadingEnhanced(false);
    }
  };

  const resetEnhancement = () => {
    setEnhancedImage(null);
    setEnhanceError('');
    setSelectedPreset(null);
    setCustomEnhancePrompt('');
  };

  // Enhance Modal
  const EnhanceModal = () => {
    if (!showEnhanceModal || !enhanceImage) return null;
    
    return (
      <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={closeEnhanceModal}>
        <div className="bg-white rounded-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b bg-gray-50">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gray-900 rounded-xl flex items-center justify-center">
                <Wand2 className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Enhance Image</h3>
                <p className="text-gray-500 text-sm">Transform with AI</p>
              </div>
            </div>
            <button onClick={closeEnhanceModal} disabled={isEnhancing || isUploadingEnhanced} className="p-2 hover:bg-gray-200 rounded-lg disabled:opacity-50">
              <X className="w-5 h-5 text-gray-600" />
            </button>
          </div>
          
          {/* Content */}
          <div className="flex-1 overflow-auto p-6">
            {/* Before / After */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-4">
              {/* Original */}
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-gray-400" />
                  <span className="text-xs font-medium text-gray-600">Original</span>
                </div>
                <div className="relative bg-gray-100 rounded-xl overflow-hidden aspect-square flex items-center justify-center">
                  <img src={enhanceImage.url || enhanceImage.url_128} alt="Original" className="max-w-full max-h-full object-contain" />
                </div>
              </div>

              {/* Enhanced */}
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-emerald-500" />
                  <span className="text-xs font-medium text-gray-600">Enhanced</span>
                </div>
                <div className="relative bg-gray-100 rounded-xl overflow-hidden aspect-square flex items-center justify-center">
                  {isEnhancing ? (
                    <div className="text-center">
                      <Loader2 className="w-10 h-10 text-blue-600 animate-spin mx-auto" />
                      <p className="text-gray-500 mt-3 text-sm">Enhancing...</p>
                    </div>
                  ) : enhancedImage ? (
                    <img src={`data:${enhancedImage.mimeType || 'image/png'};base64,${enhancedImage.data}`} alt="Enhanced" className="max-w-full max-h-full object-contain" />
                  ) : enhanceError ? (
                    <div className="text-center p-4">
                      <X className="w-8 h-8 text-red-500 mx-auto mb-2" />
                      <p className="text-red-600 text-sm">{enhanceError}</p>
                    </div>
                  ) : (
                    <div className="text-center">
                      <Sparkles className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                      <p className="text-gray-400 text-sm">Select preset & enhance</p>
                    </div>
                  )}
                </div>
                {enhancedImage && (
                  <div className="flex gap-2">
                    <button onClick={downloadEnhanced} className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-gray-900 text-white rounded-lg text-xs font-medium hover:bg-gray-800">
                      <Download className="w-3.5 h-3.5" /> Download
                    </button>
                    <button onClick={uploadEnhancedToMeta} disabled={isUploadingEnhanced} className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-blue-600 text-white rounded-lg text-xs font-medium hover:bg-blue-700 disabled:opacity-50">
                      {isUploadingEnhanced ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                      {isUploadingEnhanced ? 'Uploading...' : 'Upload to Meta'}
                    </button>
                    <button onClick={resetEnhancement} className="py-2 px-3 border border-gray-200 rounded-lg hover:bg-gray-50 text-xs">
                      <RotateCcw className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Presets */}
            {!enhancedImage && (
              <>
                <div className="mb-3">
                  <div className="flex flex-wrap gap-1.5">
                    {ENHANCEMENT_PRESETS.map((preset) => (
                      <button
                        key={preset.id}
                        onClick={() => { setSelectedPreset(preset.id); setCustomEnhancePrompt(''); }}
                        disabled={isEnhancing}
                        className={`px-2 py-1 rounded text-[10px] font-medium transition-all disabled:opacity-50 ${
                          selectedPreset === preset.id ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        {preset.name}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="mb-3">
                  <textarea
                    value={customEnhancePrompt}
                    onChange={(e) => { setCustomEnhancePrompt(e.target.value); if (e.target.value.trim()) setSelectedPreset(null); }}
                    placeholder="Or write custom prompt..."
                    rows={2}
                    disabled={isEnhancing}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none disabled:bg-gray-50"
                  />
                </div>

                <button
                  onClick={handleEnhance}
                  disabled={isEnhancing || (!selectedPreset && !customEnhancePrompt.trim())}
                  className="w-full flex items-center justify-center gap-2 py-2.5 bg-gray-900 text-white rounded-xl hover:bg-gray-800 disabled:opacity-50 font-medium text-sm"
                >
                  {isEnhancing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
                  {isEnhancing ? 'Enhancing...' : 'Enhance Image'}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Preview Modal
  const PreviewModal = () => {
    if (!showPreview || !selectedMedia) return null;
    
    const isVideo = activeTab === 'videos';
    
    return (
      <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => setShowPreview(false)}>
        <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden" onClick={e => e.stopPropagation()}>
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b">
            <h3 className="font-semibold text-gray-900 truncate flex-1">
              {isVideo ? (selectedMedia.title || 'Video') : (selectedMedia.name || selectedMedia.hash)}
            </h3>
            <button onClick={() => setShowPreview(false)} className="p-2 hover:bg-gray-100 rounded-lg">
              <X className="w-5 h-5" />
            </button>
          </div>
          
          {/* Content */}
          <div className="p-4 flex flex-col lg:flex-row gap-6">
            {/* Preview */}
            <div className="flex-1 bg-gray-100 rounded-xl flex items-center justify-center min-h-[300px]">
              {isVideo ? (
                selectedMedia.source ? (
                  <video 
                    src={selectedMedia.source} 
                    controls 
                    className="max-w-full max-h-[400px] rounded-lg"
                  />
                ) : selectedMedia.picture ? (
                  <img 
                    src={selectedMedia.picture} 
                    alt="Video thumbnail" 
                    className="max-w-full max-h-[400px] rounded-lg object-contain"
                  />
                ) : (
                  <Video className="w-16 h-16 text-gray-400" />
                )
              ) : (
                selectedMedia.url_128 || selectedMedia.url ? (
                  <img 
                    src={selectedMedia.url || selectedMedia.url_128} 
                    alt={selectedMedia.name || 'Image'} 
                    className="max-w-full max-h-[400px] rounded-lg object-contain"
                  />
                ) : (
                  <Image className="w-16 h-16 text-gray-400" />
                )
              )}
            </div>
            
            {/* Details */}
            <div className="lg:w-72 space-y-4">
              <h4 className="font-semibold text-gray-700 text-sm uppercase tracking-wide">Details</h4>
              
              <div className="space-y-3 text-sm">
                <div>
                  <span className="text-gray-500">ID:</span>
                  <div className="flex items-center gap-2 mt-1">
                    <code className="bg-gray-100 px-2 py-1 rounded text-xs flex-1 truncate">
                      {selectedMedia.id || selectedMedia.hash}
                    </code>
                    <button 
                      onClick={() => copyToClipboard(selectedMedia.id || selectedMedia.hash, 'ID')}
                      className="p-1.5 hover:bg-gray-100 rounded"
                    >
                      <Copy className="w-4 h-4 text-gray-500" />
                    </button>
                  </div>
                </div>
                
                {isVideo && selectedMedia.length && (
                  <div>
                    <span className="text-gray-500">Duration:</span>
                    <p className="font-medium">{Math.floor(selectedMedia.length / 60)}:{String(selectedMedia.length % 60).padStart(2, '0')}</p>
                  </div>
                )}
                
                {!isVideo && selectedMedia.width && selectedMedia.height && (
                  <div>
                    <span className="text-gray-500">Dimensions:</span>
                    <p className="font-medium">{selectedMedia.width} x {selectedMedia.height} px</p>
                  </div>
                )}
                
                {selectedMedia.created_time && (
                  <div>
                    <span className="text-gray-500">Created:</span>
                    <p className="font-medium">{formatDate(selectedMedia.created_time)}</p>
                  </div>
                )}
                
                {selectedMedia.updated_time && (
                  <div>
                    <span className="text-gray-500">Updated:</span>
                    <p className="font-medium">{formatDate(selectedMedia.updated_time)}</p>
                  </div>
                )}
              </div>
              
              {/* Actions */}
              <div className="pt-4 border-t space-y-2">
                {(selectedMedia.url || selectedMedia.source) && (
                  <a 
                    href={selectedMedia.url || selectedMedia.source} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 w-full py-2.5 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <Download className="w-4 h-4" />
                    {isVideo ? 'Download Video' : 'Download Image'}
                  </a>
                )}
                <button 
                  onClick={() => copyToClipboard(selectedMedia.url || selectedMedia.source || selectedMedia.id, 'URL')}
                  className="flex items-center justify-center gap-2 w-full py-2.5 px-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <Copy className="w-4 h-4" />
                  Copy URL
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Loading state
  if (connectionLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-12 h-12 text-blue-600 animate-spin" />
          <p className="text-gray-600 text-lg animate-pulse">Loading Meta Media Library...</p>
        </div>
      </div>
    );
  }

  // No connection state
  if (!adAccountId) {
    return (
      <div className="p-6">
        <div className="bg-white rounded-2xl shadow-sm p-8 max-w-md mx-auto text-center">
          <div className="p-3 bg-white border border-gray-100 shadow-sm rounded-xl w-fit mx-auto mb-4">
            <img 
              src="/6033716.png" 
              alt="Meta Logo" 
              className="w-10 h-10 object-contain opacity-50"
            />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Meta Ads Connection Required</h2>
          <p className="text-gray-500 mb-6">
            Please connect your Meta Ads account to access the Media Library.
          </p>
          <a 
            href="/settings" 
            className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors font-medium"
          >
            Go to Settings
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Modals */}
      <PreviewModal />
      <UploadModal />
      <AIModal />
      <EnhanceModal />
      
      {/* Header */}
      <div className="flex justify-between items-center bg-white p-5 rounded-2xl shadow-sm">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-white border border-gray-100 shadow-sm rounded-xl">
            <img 
              src="/6033716.png" 
              alt="Meta Logo" 
              className="w-8 h-8 object-contain"
            />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-gray-900">Meta Media Library</h1>
            <p className="text-gray-500 text-sm">{adAccountName}</p>
          </div>
        </div>
      </div>

      {/* Main Card */}
      <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
        {/* Toolbar */}
        <div className="p-4 border-b border-gray-100 flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
          {/* Tabs */}
          <div className="flex bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setActiveTab('images')}
              className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${
                activeTab === 'images' 
                  ? 'bg-white text-blue-600 shadow-sm' 
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <Image className="w-4 h-4" />
              Images
              <span className={`px-2 py-0.5 rounded-full text-xs ${
                activeTab === 'images' ? 'bg-blue-100 text-blue-700' : 'bg-gray-200 text-gray-600'
              }`}>
                {images.length}
              </span>
            </button>
            <button
              onClick={() => setActiveTab('videos')}
              className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${
                activeTab === 'videos' 
                  ? 'bg-white text-blue-600 shadow-sm' 
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <Video className="w-4 h-4" />
              Videos
              <span className={`px-2 py-0.5 rounded-full text-xs ${
                activeTab === 'videos' ? 'bg-blue-100 text-blue-700' : 'bg-gray-200 text-gray-600'
              }`}>
                {videos.length}
              </span>
            </button>
          </div>
          
          {/* Right Controls */}
          <div className="flex items-center gap-3 w-full sm:w-auto">
            {/* Search */}
            <div className="relative flex-1 sm:flex-initial">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full sm:w-64 pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            
            {/* Select Mode Toggle */}
            <button
              onClick={() => (selectMode ? exitSelectMode() : setSelectMode(true))}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${
                selectMode
                  ? 'bg-blue-600 text-white border-blue-600 hover:bg-blue-700'
                  : 'border-gray-200 text-gray-700 hover:bg-gray-50'
              }`}
              title="Görselleri seçip toplu indir"
            >
              <CheckSquare className="w-4 h-4" />
              <span className="hidden sm:inline">{selectMode ? 'Seçimi Kapat' : 'Seç'}</span>
            </button>

            {/* View Toggle */}
            <div className="flex bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded-md ${viewMode === 'grid' ? 'bg-white shadow-sm' : ''}`}
              >
                <Grid3X3 className={`w-4 h-4 ${viewMode === 'grid' ? 'text-blue-600' : 'text-gray-500'}`} />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded-md ${viewMode === 'list' ? 'bg-white shadow-sm' : ''}`}
              >
                <List className={`w-4 h-4 ${viewMode === 'list' ? 'text-blue-600' : 'text-gray-500'}`} />
              </button>
            </div>
            
            {/* Refresh */}
            <button
              onClick={handleRefresh}
              disabled={isLoading}
              className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 text-gray-600 ${isLoading ? 'animate-spin' : ''}`} />
            </button>
            
            {/* Upload Button */}
            <button
              onClick={() => setShowUploadModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors text-sm font-medium"
            >
              <Upload className="w-4 h-4" />
              <span className="hidden sm:inline">Upload</span>
            </button>
            
            {/* AI Create Button */}
            <button
              onClick={() => setShowAIModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
            >
              <Sparkles className="w-4 h-4" />
              <span className="hidden sm:inline">AI Create</span>
            </button>
          </div>
        </div>

        {/* Selection Action Bar */}
        {selectMode && (
          <div className="px-4 py-3 border-b border-blue-100 bg-blue-50 flex flex-wrap items-center gap-3">
            <button
              onClick={toggleSelectAllShown}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-blue-700 hover:bg-blue-100 rounded-lg transition-colors"
            >
              {allShownSelected ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />}
              {allShownSelected ? 'Tüm seçimi kaldır' : 'Görünenleri seç'}
            </button>
            <span className="text-sm text-blue-800 font-medium">
              {selectedKeys.size} seçili
            </span>
            <div className="flex-1" />
            {selectedKeys.size > 0 && (
              <button
                onClick={clearSelection}
                className="px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Temizle
              </button>
            )}
            <button
              onClick={downloadSelected}
              disabled={isBulkDownloading || selectedKeys.size === 0}
              className="flex items-center gap-2 px-4 py-1.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isBulkDownloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              Seçili indir ({selectedKeys.size})
            </button>
          </div>
        )}

        {/* Content */}
        <div className="p-4 min-h-[400px]">
          {isLoading && currentMedia.length === 0 ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <Loader2 className="w-8 h-8 text-blue-600 animate-spin mx-auto mb-3" />
                <p className="text-gray-500">Loading...</p>
              </div>
            </div>
          ) : currentMedia.length === 0 ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  {activeTab === 'images' ? (
                    <Image className="w-8 h-8 text-gray-400" />
                  ) : (
                    <Video className="w-8 h-8 text-gray-400" />
                  )}
                </div>
                <h3 className="font-medium text-gray-900 mb-1">
                  {searchTerm ? 'No results found' : `No ${activeTab === 'images' ? 'images' : 'videos'} yet`}
                </h3>
                <p className="text-gray-500 text-sm">
                  {searchTerm ? 'Try a different search term' : 'Upload media from Meta Ads Manager'}
                </p>
              </div>
            </div>
          ) : viewMode === 'grid' ? (
            /* Grid View */
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
              {currentMedia.map((item) => (
                <div
                  key={item.id || item.hash}
                  onClick={() => {
                    if (selectMode) { toggleSelect(item); return; }
                    setSelectedMedia(item);
                    setShowPreview(true);
                  }}
                  className={`group relative aspect-square bg-gray-100 rounded-xl overflow-hidden cursor-pointer transition-all ${
                    selectMode && selectedKeys.has(getKey(item))
                      ? 'ring-2 ring-blue-600'
                      : 'hover:ring-2 hover:ring-blue-500'
                  }`}
                >
                    {selectMode && (
                      <div
                        className="absolute top-2 left-2 z-20"
                        onClick={(e) => { e.stopPropagation(); toggleSelect(item); }}
                      >
                        {selectedKeys.has(getKey(item)) ? (
                          <CheckSquare className="w-6 h-6 text-blue-600 bg-white rounded shadow" />
                        ) : (
                          <Square className="w-6 h-6 text-white bg-black/30 rounded shadow" />
                        )}
                      </div>
                    )}
                    {activeTab === 'images' ? (
                      <img
                        src={item.url || item.url_128}
                        alt={item.name || 'Image'}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    ) : (
                      <>
                        {item.picture ? (
                          <img
                            src={item.picture}
                            alt={item.title || 'Video'}
                            className="w-full h-full object-cover"
                            loading="lazy"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Video className="w-8 h-8 text-gray-400" />
                          </div>
                        )}
                        {/* Video duration badge */}
                        {item.length && (
                          <div className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-1.5 py-0.5 rounded">
                            {Math.floor(item.length / 60)}:{String(item.length % 60).padStart(2, '0')}
                          </div>
                        )}
                      </>
                    )}
                    
                    {/* Hover overlay */}
                    <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-2 p-2">
                      <button
                        onClick={(e) => { e.stopPropagation(); setSelectedMedia(item); setShowPreview(true); }}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-white/90 text-gray-900 rounded-lg text-xs font-medium hover:bg-white"
                      >
                        <Eye className="w-3.5 h-3.5" /> View
                      </button>
                    {activeTab === 'images' && (
                      <button
                        onClick={(e) => { e.stopPropagation(); openEnhanceModal(item); }}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-medium hover:bg-blue-700"
                      >
                        <Wand2 className="w-3.5 h-3.5" /> Enhance
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            /* List View */
            <div className="space-y-2">
              {currentMedia.map((item) => (
                <div
                  key={item.id || item.hash}
                  onClick={() => {
                    if (selectMode) { toggleSelect(item); return; }
                    setSelectedMedia(item);
                    setShowPreview(true);
                  }}
                  className={`flex items-center gap-4 p-3 rounded-xl cursor-pointer transition-colors ${
                    selectMode && selectedKeys.has(getKey(item))
                      ? 'bg-blue-50 ring-2 ring-blue-500'
                      : 'bg-gray-50 hover:bg-gray-100'
                  }`}
              >
                  {selectMode && (
                    <div
                      className="flex-shrink-0"
                      onClick={(e) => { e.stopPropagation(); toggleSelect(item); }}
                    >
                      {selectedKeys.has(getKey(item)) ? (
                        <CheckSquare className="w-5 h-5 text-blue-600" />
                      ) : (
                        <Square className="w-5 h-5 text-gray-400" />
                      )}
                    </div>
                  )}
                  {/* Thumbnail */}
                  <div className="w-16 h-16 bg-gray-200 rounded-lg overflow-hidden flex-shrink-0">
                    {activeTab === 'images' ? (
                      <img
                        src={item.url || item.url_128}
                        alt={item.name || 'Image'}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    ) : item.picture ? (
                      <img
                        src={item.picture}
                        alt={item.title || 'Video'}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Video className="w-6 h-6 text-gray-400" />
                      </div>
                    )}
                  </div>
                  
                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-gray-900 truncate">
                      {activeTab === 'images' ? (item.name || item.hash) : (item.title || item.id)}
                    </h4>
                    <div className="flex items-center gap-4 mt-1 text-xs text-gray-500">
                      {activeTab === 'images' && item.width && item.height && (
                        <span>{item.width} x {item.height}</span>
                      )}
                      {activeTab === 'videos' && item.length && (
                        <span>{Math.floor(item.length / 60)}:{String(item.length % 60).padStart(2, '0')}</span>
                      )}
                      {item.created_time && (
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {formatDate(item.created_time)}
                        </span>
                      )}
                    </div>
                  </div>
                  
                  {/* Actions */}
                  <div className="flex items-center gap-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        copyToClipboard(item.id || item.hash, 'ID');
                      }}
                      className="p-2 hover:bg-white rounded-lg transition-colors"
                    >
                      <Copy className="w-4 h-4 text-gray-500" />
                    </button>
                    {activeTab === 'images' && (
                      <button
                        onClick={(e) => { e.stopPropagation(); openEnhanceModal(item); }}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-medium hover:bg-blue-700"
                      >
                        <Wand2 className="w-3.5 h-3.5" /> Enhance
                      </button>
                    )}
                    <Eye className="w-4 h-4 text-gray-400" />
                  </div>
                </div>
              ))}
            </div>
          )}
          
          {/* Load More */}
          {hasMore && (
            <div className="mt-6 text-center">
              <button
                onClick={loadMore}
                disabled={isLoading}
                className="inline-flex items-center gap-2 px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors font-medium"
              >
                {isLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <ChevronRight className="w-4 h-4" />
                )}
                Load More
              </button>
            </div>
          )}
        </div>

        {/* Footer Stats */}
        <div className="p-4 border-t border-gray-100 bg-gray-50 rounded-b-2xl">
          <div className="flex items-center justify-between text-sm text-gray-600">
            <span>
              {activeTab === 'images' ? filteredImages.length : filteredVideos.length} {activeTab === 'images' ? 'images' : 'videos'} shown
            </span>
            <span className="text-xs text-gray-400">
              Ad Account ID: {adAccountId}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

